import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import api from './api/client'

const AuthCtx = createContext(null)

function load(key) {
  try { return JSON.parse(localStorage.getItem(key) || 'null') } catch { return null }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => load('user'))
  const [token, setToken] = useState(() => localStorage.getItem('token'))
  const [licenciado, setLic] = useState(() => load('licenciado'))
  const [config, setConfig] = useState(() => load('config') || {})
  const real = load('real') // backup do admin enquanto impersona ({token, user}) ou null

  // Config global (ex.: loja_habilitada) — carrega 1x quando há sessão
  useEffect(() => {
    if (!token) return
    api.get('/parametros-mlm').then(({ data }) => {
      setConfig(data || {})
      try { localStorage.setItem('config', JSON.stringify(data || {})) } catch { /* */ }
    }).catch(() => {})
  }, [token])

  // Status da licença SEMPRE fresco do servidor no boot (evita travar licenciado PAGO por
  // dado ausente/desatualizado no localStorage ou vindo do "Ver como"). /auth/me traz licenca_status.
  useEffect(() => {
    if (!token || load('user')?.perfil !== 'licenciado') return
    refreshLicenciado()
  }, [token]) // eslint-disable-line react-hooks/exhaustive-deps

  function _persistLic(lic) {
    if (lic) localStorage.setItem('licenciado', JSON.stringify(lic))
    else localStorage.removeItem('licenciado')
    setLic(lic || null)
  }

  function _aplicarSessao(data) {
    localStorage.setItem('token', data.access_token)
    localStorage.setItem('user', JSON.stringify(data.usuario))
    _persistLic(data.licenciado)
    localStorage.removeItem('real')
    setToken(data.access_token)
    setUser(data.usuario)
    return data
  }

  // identificador = CPF OU e-mail (PRD)
  async function login(identificador, senha) {
    const { data } = await api.post('/auth/login', { login: identificador, email: identificador, senha })
    return _aplicarSessao(data)
  }

  // Usado por primeiro-acesso / redefinir-senha (o backend já devolve a sessão)
  function aplicarSessao(data) { return _aplicarSessao(data) }

  // Recarrega o licenciado (status da licença) — ex.: após confirmar pagamento
  const refreshLicenciado = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me')
      if (data?.licenciado) _persistLic(data.licenciado)
      return data?.licenciado || null
    } catch { return null }
  }, [])

  function logout() {
    localStorage.clear()
    setToken(null)
    setUser(null)
    setLic(null)
  }

  async function verComo(tipo, id) {
    const adminToken = load('real')?.token || localStorage.getItem('token')
    const { data } = await api.post('/admin/ver-como', { tipo, id },
      { headers: { Authorization: `Bearer ${adminToken}` } })
    if (!load('real')) {
      localStorage.setItem('real', JSON.stringify({ token: adminToken, user: load('user') }))
    }
    localStorage.setItem('token', data.access_token)
    localStorage.setItem('user', JSON.stringify(data.usuario))
    _persistLic(data.licenciado)
    window.location.hash = '#/dashboard'
    window.location.reload()
  }

  function sairSimulacao() {
    const r = load('real')
    if (!r) return
    localStorage.setItem('token', r.token)
    localStorage.setItem('user', JSON.stringify(r.user))
    localStorage.removeItem('licenciado')
    localStorage.removeItem('real')
    window.location.hash = '#/dashboard'
    window.location.reload()
  }

  const isAdminReal = (real ? real.user?.perfil : user?.perfil) === 'admin'
  // Pré-licenciado: licenciado com licença EXPLICITAMENTE pendente. Se licenca_status vier
  // ausente/desconhecido (ex.: payload reduzido), NÃO bloqueia — evita travar quem já pagou.
  const preLicenciado = !!(user?.perfil === 'licenciado' && licenciado
    && licenciado.licenca_status && licenciado.licenca_status !== 'ativa')
  // Loja de produtos físicos habilitada globalmente (admin controla). Admin sempre acessa.
  const lojaHabilitada = user?.perfil === 'admin' ? true : (config?.loja_habilitada !== false)

  return (
    <AuthCtx.Provider value={{
      user, token, licenciado, preLicenciado, config, lojaHabilitada,
      login, aplicarSessao, refreshLicenciado, logout,
      verComo, sairSimulacao, isAdminReal, impersonating: !!real,
    }}>
      {children}
    </AuthCtx.Provider>
  )
}

export const useAuth = () => useContext(AuthCtx)
